import { openDB } from 'idb';

export type SavedEventRecord = {
  id: string;
  cachedEvent: Record<string, any>;
  createdAt: number;
  updatedAt: number;
  lastSeenAt: number;
  ended?: boolean;
  unavailable?: boolean;
};

export type SavedEventsData = {
  schemaVersion: number;
  items: SavedEventRecord[];
  exportedAt: number;
};

export const defaultSavedEventsSchemaVersion = 1;

const DB_NAME = 'frolik-device-saved-events';
const STORE_NAME = 'saved-events';
const STORAGE_KEY = 'frolik_saved_events_v1';

function normalizeEventId(eventId: string | number) {
  return String(eventId || '').trim();
}

function isBrowser() {
  return typeof window !== 'undefined';
}

function safeJsonParse<T>(value: string | null, fallback: T): T {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn('Could not parse saved events JSON:', error);
    return fallback;
  }
}

function readFallbackData(): SavedEventsData {
  if (!isBrowser()) {
    return {
      schemaVersion: defaultSavedEventsSchemaVersion,
      items: [],
      exportedAt: Date.now(),
    };
  }

  return safeJsonParse<SavedEventsData>(window.localStorage.getItem(STORAGE_KEY), {
    schemaVersion: defaultSavedEventsSchemaVersion,
    items: [],
    exportedAt: Date.now(),
  });
}

function writeFallbackData(data: SavedEventsData) {
  if (!isBrowser()) {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

async function persistStorageState() {
  if (!('storage' in navigator) || !navigator.storage?.persist) {
    return;
  }

  try {
    await navigator.storage.persist();
  } catch (error) {
    console.warn('Storage persistence request failed:', error);
  }
}

async function openSavedDb() {
  if (!isBrowser()) {
    return null;
  }

  try {
    return await openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      },
    });
  } catch (error) {
    console.warn('IndexedDB unavailable; using browser fallback storage:', error);
    return null;
  }
}

function toRecord(event: Record<string, any>): SavedEventRecord {
  const eventId = normalizeEventId(event?.id || event?.slug || event?._id || '');

  return {
    id: eventId,
    cachedEvent: { ...event },
    createdAt: Date.now(),
    updatedAt: Date.now(),
    lastSeenAt: Date.now(),
    ended: false,
    unavailable: false,
  };
}

async function readDbRecords(): Promise<SavedEventRecord[]> {
  const db = await openSavedDb();

  if (!db) {
    const fallback = readFallbackData();
    return Array.isArray(fallback.items) ? fallback.items : [];
  }

  return (await db.getAll(STORE_NAME)) as SavedEventRecord[];
}

const savedEventsStore = {
  async add(event: Record<string, any>) {
    if (!event) {
      return null;
    }

    const record = toRecord(event);
    const db = await openSavedDb();

    if (!db) {
      const fallback = readFallbackData();
      const items = Array.isArray(fallback.items) ? fallback.items : [];
      const next = [...items.filter((item) => item.id !== record.id), record];
      writeFallbackData({
        schemaVersion: defaultSavedEventsSchemaVersion,
        items: next,
        exportedAt: Date.now(),
      });
      persistStorageState();
      return record;
    }

    await db.put(STORE_NAME, record);
    persistStorageState();
    return record;
  },

  async remove(eventId: string | number) {
    const id = normalizeEventId(eventId);
    if (!id) {
      return false;
    }

    const db = await openSavedDb();

    if (!db) {
      const fallback = readFallbackData();
      const next = (Array.isArray(fallback.items) ? fallback.items : []).filter((item) => item.id !== id);
      writeFallbackData({
        schemaVersion: defaultSavedEventsSchemaVersion,
        items: next,
        exportedAt: Date.now(),
      });
      return true;
    }

    await db.delete(STORE_NAME, id);
    return true;
  },

  async has(eventId: string | number) {
    const id = normalizeEventId(eventId);
    if (!id) {
      return false;
    }

    const db = await openSavedDb();

    if (!db) {
      const fallback = readFallbackData();
      return (Array.isArray(fallback.items) ? fallback.items : []).some((item) => item.id === id);
    }

    const value = await db.get(STORE_NAME, id);
    return Boolean(value);
  },

  async list() {
    return readDbRecords();
  },

  async clear() {
    const db = await openSavedDb();

    if (!db) {
      writeFallbackData({
        schemaVersion: defaultSavedEventsSchemaVersion,
        items: [],
        exportedAt: Date.now(),
      });
      return true;
    }

    await db.clear(STORE_NAME);
    return true;
  },

  async exportJson() {
    const items = await this.list();
    const payload: SavedEventsData = {
      schemaVersion: defaultSavedEventsSchemaVersion,
      items,
      exportedAt: Date.now(),
    };

    writeFallbackData(payload);
    return JSON.stringify(payload, null, 2);
  },

  async importJson(rawJson: string) {
    const parsed = safeJsonParse<SavedEventsData>(rawJson, {
      schemaVersion: defaultSavedEventsSchemaVersion,
      items: [],
      exportedAt: Date.now(),
    });

    const migratedItems = Array.isArray(parsed.items) ? parsed.items : [];
    const result: SavedEventsData = {
      schemaVersion: defaultSavedEventsSchemaVersion,
      items: migratedItems.map((item) => ({
        id: normalizeEventId(item?.id || item?.cachedEvent?.id || ''),
        cachedEvent: item?.cachedEvent || {},
        createdAt: Number(item?.createdAt || Date.now()),
        updatedAt: Number(item?.updatedAt || Date.now()),
        lastSeenAt: Number(item?.lastSeenAt || Date.now()),
        ended: Boolean(item?.ended),
        unavailable: Boolean(item?.unavailable),
      })),
      exportedAt: Date.now(),
    };

    const db = await openSavedDb();

    if (!db) {
      writeFallbackData(result);
      return result;
    }

    for (const record of result.items) {
      if (record.id) {
        await db.put(STORE_NAME, record);
      }
    }

    return result;
  },
};

export { savedEventsStore };
export const defaultSavedEventsData = {
  schemaVersion: defaultSavedEventsSchemaVersion,
  items: [],
  exportedAt: Date.now(),
};
